import * as React from 'react';
import { useCallback, useState } from 'react';

import type { UserSearchFilter } from 'search-filter/types';
import { Link, LinkContainer } from 'components/common/router';
import ShareButton from 'components/common/ShareButton';
import Icon from 'components/common/Icon';
import { ButtonToolbar, Button, DropdownButton, DeleteMenuItem } from 'components/bootstrap';
import Routes from 'routing/Routes';
import UserFilterShareModal from 'search-filter/components/UserFilterShareModal';
import useSearchFilterUsagesSummaryQuery from 'search-filter/hooks/useSearchFilterUsagesSummaryQuery';
import useUserSearchFilterMutation from 'search-filter/hooks/useUserSearchFilterMutation';
import confirmActionUsagesAlert from 'search-filter/confirmActionUsagesAlert';
import useSendTelemetry from 'logic/telemetry/useSendTelemetry';
import { TELEMETRY_EVENT_TYPE } from 'telemetry/Constants';
import usePluggableEntitySharedActions from 'hooks/usePluggableEntitySharedActions';
import usePluggableEntityTableElements from 'hooks/usePluggableEntityTableElements';
import useCurrentUser from 'hooks/useCurrentUser';
import { isPermitted } from 'util/PermissionsMixin';
import { IfPermitted } from 'components/common';

const Title = ({ filter }: { filter: UserSearchFilter }) => (
  <Link to={Routes.pluginRoute('MY-FILTERS_DETAILS_FILTERID')(filter.id)}>{filter.title || 'No Title'}</Link>
);

type Props = {
  filter: UserSearchFilter;
};

const FiltersOverviewItem = ({ filter }: Props) => {
  const [showFilterShareModal, setShowFilterShareModal] = useState<boolean>(false);
  const { isFetching: isFetchingUsagesSummary, refetch } = useSearchFilterUsagesSummaryQuery(filter.id, {
    enabled: false,
  });
  const { delete: deleteFilter } = useUserSearchFilterMutation();
  const sendTelemetry = useSendTelemetry();
  const { actions: pluggableActions, actionModals: pluggableActionModals } = usePluggableEntitySharedActions<
    Props['filter']
  >(filter, 'search_filter');
  const { getPluggableTableCells } = usePluggableEntityTableElements<UserSearchFilter & { id: string }>(
    null,
    'search_filter',
  );
  const currentUser = useCurrentUser();

  const toggleShareModalOpen = useCallback(() => {
    setShowFilterShareModal((cur) => !cur);
  }, [setShowFilterShareModal]);

  const onDeleteMyFilter = useCallback(() => {
    sendTelemetry(TELEMETRY_EVENT_TYPE.SEARCH_FILTER_DELETED, {
      app_pathname: 'my-filters',
      app_section: 'search-filter',
    });

    if (!isFetchingUsagesSummary) {
      refetch().then(({ data: { summary: usagesSummary } }) => {
        if (
          usagesSummary?.length &&
          !confirmActionUsagesAlert({
            action: 'Deleting',
            usagesSummary,
            additionalExplanation:
              '\nThe filter will be removed from entities where you are the owner and transformed to an inline filter where you are not an owner',
          })
        ) {
          return Promise.resolve();
        }

        return deleteFilter(filter.id)?.then((res) => res);
      });
    }
  }, [isFetchingUsagesSummary, refetch, deleteFilter, filter.id, sendTelemetry]);

  const moreActions = [
    pluggableActions.length ? pluggableActions : null,
    isPermitted(currentUser.permissions, `search_filters:delete:${filter.id}`) ? (
      <DeleteMenuItem onSelect={onDeleteMyFilter} key="delete-menu-item" />
    ) : null,
  ].filter(Boolean);

  return (
    <tr>
      <td>
        <Title filter={filter} />
      </td>
      <td>{filter.queryString}</td>
      <td>{filter.description || <i>No description given.</i>}</td>
      <td>{getPluggableTableCells(filter.id)}</td>
      <td>
        <ButtonToolbar className="pull-right">
          <ShareButton entityType="search_filter" entityId={filter.id} onClick={toggleShareModalOpen} bsSize="xs" />
          <IfPermitted permissions={`search_filters:edit:${filter.id}`}>
            <LinkContainer to={Routes.pluginRoute('MY-FILTERS_EDIT_FILTERID')(filter.id)}>
              <Button bsSize="xs">
                <Icon name="edit_square" /> Edit
              </Button>
            </LinkContainer>
          </IfPermitted>
          {!!moreActions.length && (
            <DropdownButton
              key="more-actions-button"
              title="More actions"
              bsSize="xs"
              pullRight
              id={`more-actions-dropdown-${filter.id}`}>
              {moreActions}
            </DropdownButton>
          )}
        </ButtonToolbar>
        {showFilterShareModal && <UserFilterShareModal filter={filter} toggleShareModalOpen={toggleShareModalOpen} />}
        {pluggableActionModals}
      </td>
    </tr>
  );
};

export default FiltersOverviewItem;
